// Feeding plan: how many meals, when, how much, and when the bag runs out.

import { mealsPerDay } from './age.ts';
import { MINUTE, addDays, atClock, dayStart, daysBetween, formatTime, startOfDay, today } from './time.ts';
import type { FoodConfig, Household, ISODate, LogEvent, Millis } from './types.ts';

export const DEFAULT_MEAL_TIMES: Record<2 | 3 | 4, string[]> = {
  4: ['07:00', '11:00', '15:00', '18:30'],
  3: ['07:00', '12:30', '18:00'],
  2: ['07:30', '17:30'],
};

/**
 * Last meal → bed: about two hours. Enough to digest and empty out on the last
 * trip, without a long hungry stretch overnight. (AKC: avoid feeding within 2
 * hours of bedtime; PDSA: evening meal, toilet, calm play, last toilet; a
 * behaviourist for KC Insurance: not too late, and the last wee as late as you
 * can.) The app flags a dinner well under that, less than 90 minutes before bed.
 */
export const LAST_MEAL_GAP = 120 * MINUTE;
export const LATE_DINNER = 90 * MINUTE;

const clockMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

export interface LastMealCheck {
  last: string;
  bed: string;
  /** Minutes from the last meal to bedtime. */
  gap: number;
  ok: boolean;
  text: string;
}

/** Does the planned last meal leave about two hours before bed? */
export function lastMealCheck(times: string[], nightStart: string): LastMealCheck | undefined {
  const last = [...times].filter(Boolean).sort().at(-1);
  if (!last || !nightStart) return undefined;
  let gap = clockMinutes(nightStart) - clockMinutes(last);
  if (gap < -6 * 60) gap += 24 * 60; // bedtime after midnight
  const hrs = (m: number) => (m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60} hour${m === 60 ? '' : 's'}`);
  const ok = gap >= LAST_MEAL_GAP / MINUTE - 15;
  const text =
    gap <= 0
      ? `His last meal (${last}) is at or after bedtime (${nightStart}). Aim for about 2 hours between them: move bedtime later, or the meal earlier.`
      : ok
        ? `Last meal ${last}, bed ${nightStart}: ${hrs(gap)} to digest and empty out — right where it should be. Last wee as late as you can, just before bed.`
        : `Last meal ${last} is only ${hrs(gap)} before bed (${nightStart}). Aim for about 2 hours: keep the meal and make bedtime a little later (Settings → Household), or move the meal earlier.`;
  return { last, bed: nightStart, gap, ok, text };
}

export interface PlannedMeal {
  index: number;
  time: string;
  at: Millis;
  grams?: number;
  eaten?: LogEvent<'meal'>;
}

export interface MealPlan {
  perDay: 2 | 3 | 4;
  times: string[];
  gramsPerMeal?: number;
  meals: PlannedMeal[];
  next?: PlannedMeal;
  eatenToday: number;
  gramsToday: number;
}

export function mealTimes(household: Pick<Household, 'mealTimes'> | undefined, perDay: 2 | 3 | 4): string[] {
  const custom = household?.mealTimes?.[perDay];
  return custom && custom.length === perDay ? custom : DEFAULT_MEAL_TIMES[perDay];
}

/**
 * Build today's meal plan and match logged meals to slots, in order. A meal
 * fills the earliest unfilled slot — so a late breakfast still counts as
 * breakfast — unless it's within half an hour of a later slot (or after it):
 * then the slots before were missed or not logged, and it's that later meal.
 */
export function mealPlan(opts: {
  ageWeeks: number;
  food: FoodConfig;
  household?: Pick<Household, 'mealTimes'>;
  events: LogEvent[];
  now: Millis;
}): MealPlan {
  const perDay = mealsPerDay(opts.ageWeeks);
  const times = mealTimes(opts.household, perDay);
  const gramsPerMeal = opts.food.dailyGrams ? Math.round(opts.food.dailyGrams / perDay) : undefined;
  const todayStart = startOfDay(opts.now);
  const todaysMeals = opts.events
    .filter((e): e is LogEvent<'meal'> => e.type === 'meal' && e.at >= todayStart && e.at < dayStart(opts.now, 1))
    .sort((a, b) => a.at - b.at);

  const meals: PlannedMeal[] = times.map((time, index) => ({ index, time, at: atClock(opts.now, time), grams: gramsPerMeal }));
  let slot = 0;
  for (const m of todaysMeals) {
    while (slot < meals.length - 1 && m.at >= meals[slot + 1].at - 30 * MINUTE) slot++;
    if (slot >= meals.length) break;
    meals[slot].eaten = m;
    slot++;
  }
  // Slots before the last meal eaten were missed (or not logged): the next meal is after it.
  const lastEaten = meals.reduce((n, m, i) => (m.eaten ? i : n), -1);
  const next = meals.find((m, i) => !m.eaten && i > lastEaten);
  const gramsToday = todaysMeals.reduce((sum, m) => sum + (m.data.grams ?? gramsPerMeal ?? 0), 0);
  return { perDay, times, gramsPerMeal, meals, next, eatenToday: todaysMeals.length, gramsToday };
}

/**
 * The meal to show on the Meal tile: the next slot that's still ahead (or only
 * just passed). A lunch missed hours ago isn't "next" any more — that's for
 * Up next to ask about — so the tile moves on to the afternoon meal, then to
 * tomorrow's breakfast once today's are over. (Until the next meal is nearly
 * due, a late, unlogged one stays, marked "not logged?".)
 */
export function upcomingMeal(plan: Pick<MealPlan, 'meals' | 'next' | 'times'>, now: Millis): { meal?: PlannedMeal; label: string; missed?: boolean } {
  const next = plan.next;
  // A meal that's late but not logged stays the question ("11:00 · not logged?") until the
  // next one is nearly due — lunch quietly turning into "17:00" looks like it was forgotten.
  if (next && !next.eaten && next.at <= now - 45 * MINUTE) {
    const later = plan.meals.find((m) => m.index > next.index && !m.eaten);
    if (later ? now < later.at - 60 * MINUTE : now < next.at + 3 * 60 * MINUTE) return { meal: next, label: `${next.time} · not logged?`, missed: true };
  }
  const from = plan.next?.index ?? plan.meals.length;
  const meal = plan.meals.find((m) => m.index >= from && !m.eaten && m.at > now - 45 * MINUTE);
  if (meal) return { meal, label: meal.time };
  if (!plan.next) return { label: 'all done today' };
  return { label: `tomorrow ${plan.times[0]}` };
}

export function describeMeal(meal: PlannedMeal, perDay: number): string {
  const names: Record<number, string[]> = {
    4: ['Breakfast', 'Lunch', 'Afternoon', 'Dinner'],
    3: ['Breakfast', 'Lunch', 'Dinner'],
    2: ['Breakfast', 'Dinner'],
  };
  return `${names[perDay]?.[meal.index] ?? `Meal ${meal.index + 1}`} · ${formatTime(meal.at)}`;
}

export interface FoodStock {
  daysLeft: number;
  runsOut: ISODate;
  reorderBy: ISODate;
  percentLeft: number;
}

/** Estimate when the current bag runs out, assuming the daily ration is fed. */
export function foodStock(food: FoodConfig, now: Millis = Date.now()): FoodStock | undefined {
  if (!food.bagKg || !food.bagOpened || !food.dailyGrams) return undefined;
  const totalDays = Math.floor((food.bagKg * 1000) / food.dailyGrams);
  const used = Math.max(0, daysBetween(food.bagOpened, today(now)));
  const daysLeft = Math.max(0, totalDays - used);
  const runsOut = addDays(food.bagOpened, totalDays);
  return {
    daysLeft,
    runsOut,
    reorderBy: addDays(runsOut, -7),
    percentLeft: totalDays ? daysLeft / totalDays : 0,
  };
}

export interface TransitionDay {
  day: number;
  date: ISODate;
  newPercent: number;
}

/**
 * Gradual food change from the breeder's food: ~25% new for a few days,
 * then 50%, 75%, 100% over about 10 days. Pause if tummies get upset.
 */
export function transitionPlan(start: ISODate): TransitionDay[] {
  const steps = [25, 25, 25, 50, 50, 50, 75, 75, 75, 100];
  return steps.map((newPercent, i) => ({ day: i + 1, date: addDays(start, i), newPercent }));
}
